import React from 'react'
import styled from 'styled-components'
import { Link, graphql } from 'gatsby'

import Angle from '../atoms/Angle'
import Announcement from '../molecules/Announcement'
import Button from '../atoms/Button'
import Heading from '../atoms/Heading'
import Image from '../atoms/Image'
import Layout from '../organisms/Layout'
import OuterLink from '../atoms/OuterLink'
import Ranking from '../organisms/Ranking'
import SEO from '../molecules/SEO'
import useMenu from '../hooks/useMenu'
import { CategoryListItemCard } from '../organisms/CategoryList'
import { SidebarLeft, SidebarRight } from '../organisms/Sidebar'
import { MysqlCategories, MysqlRanksGroupConnection, Query } from '../graphqlTypes'

/**
 * カテゴリーごとの最大表示数
 */
const DISPLAY_RANKING_PRODUCT_MAX_NUM = 3

const Container = styled.div`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: flex-start;
    display: flex;
    margin: 30px 0 0;
  }
`

const HeroAnnouncement = styled(Announcement)`
  margin: 16px 10px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 10px 0 0;
  }
`

const Article = styled.article`
  flex: 1;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0 30px;
  }
`

const ParentCategoryHeading = styled(Heading)<{ gutterTop: boolean }>`
  && {
    margin: ${({ gutterTop }) => (gutterTop ? '44px' : '16px')} 0 12px;
    @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
      border-radius: 10px 10px 0px 0px;
      display: flex;
      font-size: 18px;
      margin: ${({ gutterTop }) => (gutterTop ? '56px' : '0')} 0 0;
    }
  }
`

const ChildCategoryList = styled.ul`
  align-items: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.light};
  display: flex;
  list-style: none;
  overflow-x: scroll;
  padding: 12px 0 16px 10px;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const ChildCategoryHeading = styled(Heading)<{ gutterTop: boolean }>`
  && {
    background: transparent;
    border-left: 3px solid ${({ theme }) => theme.palette.primary.main};
    color: ${({ theme }) => theme.palette.font.main};
    font-size: 16px;
    justify-content: start;
    margin: ${({ gutterTop }) => (gutterTop ? '36px' : '16px')} 10px 0;
    padding: 0 0 0 10px;
    @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
      border-width: 0 0 0 5px;
      display: flex;
      font-size: 18px;
      margin: ${({ gutterTop }) => (gutterTop ? '56px' : '30px')} 0 0;
      padding: 0 0 0 15px;
    }
  }
`

const RankingContainer = styled.div`
  margin: 16px 12px 0;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 24px 0 0;
  }
`

const RankingReadmore = styled(Link)`
  display: block;
  margin: 24px auto 0;
  text-decoration: none;
  width: 300px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 40px auto 0;
    width: 350px;
  }
`

const RankingReadmoreIcon = styled(Angle)`
  width: 24px;
  height: 24px;
  margin: 0 0 0 3px;
  object-fit: cover;
`

const SocialShare = styled.aside`
  border-top: 1px solid ${({ theme }) => theme.palette.border.light};
  margin: 36px 0 0;
  padding: 40px 20px 14px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const SocialShareBalloon = styled.div`
  margin: 0 auto;
  width: 250px;
`

const SocialShareButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin: 16px 0 0;
`

const SocialShareButton = styled(OuterLink)`
  display: block;
  margin: 0 0 0 12px;
  text-decoration: none;
  width: 160px;
  &:first-child {
    margin: 0;
  }
`

export default function IndexPage({ data }: { data: Query }) {
  const { allMysqlCategories, allMysqlRanks, allMysqlSurveyQuestions, site } = data
  const menu = useMenu(allMysqlCategories.edges, allMysqlRanks.group)

  return (
    <Layout logoVariant="h1" menu={menu}>
      <SEO
        image={`${process.env.GATSBY_CDN_URL}/${process.env.GATSBY_CDN_PATH}/ogp/top.png`}
        title={`${site!.siteMetadata!.title}2023 | ママリ`}
        type="website"
        url="https://award.mamari.jp"
      />
      <Image
        filename="hero.jpg"
        alt={'先輩ママが選ぶ 本当に使ってよかった！ 育児向け商品・サービス128アイテム ママリ口コミ大賞2023 大発表！'}
      />
      <HeroAnnouncement
        text="4,415件の口コミから「本当に使ってよかった」と思う128商品・サービスを発表"
        to="https://connehito.com/news/awardmamari-2023"
      />
      <Container>
        <SidebarLeft categories={menu} />
        <Article>
          {allMysqlCategories.edges
            .filter(({ node }) => node.categories && node.categories.length > 0)
            .map(({ node }, index) => {
              const children = (node.categories as MysqlCategories[]).filter((child) =>
                allMysqlRanks.group.find(({ fieldValue }) => child.mysqlId === parseInt(fieldValue as string, 10))
              )

              if (children.length < 1) return null

              return (
                children.length > 0 && (
                  <section key={node.id}>
                    <ParentCategoryHeading gutterTop={index !== 0} variant="h2">
                      {node.title}
                    </ParentCategoryHeading>
                    <ChildCategoryList>
                      {children.map((child) => (
                        <CategoryListItemCard category={child} key={child.id} to={child.fields!.pagePath!} />
                      ))}
                    </ChildCategoryList>

                    {children.map((child, index) => {
                      const ranks = allMysqlRanks.group.find(
                        ({ fieldValue }) => child.mysqlId === parseInt(fieldValue as string, 10)
                      ) as MysqlRanksGroupConnection

                      return (
                        <section key={child.id}>
                          <ChildCategoryHeading gutterTop={index !== 0} variant="h3">
                            {child.title}
                          </ChildCategoryHeading>
                          <RankingContainer>
                            <Ranking
                              ranks={ranks.edges.slice(0, DISPLAY_RANKING_PRODUCT_MAX_NUM)}
                              surveyQuestions={allMysqlSurveyQuestions.edges}
                            />
                          </RankingContainer>
                          <RankingReadmore to={child.fields!.pagePath!}>
                            <Button color="white">
                              もっとみる
                              <RankingReadmoreIcon color="pink" direction="right" />
                            </Button>
                          </RankingReadmore>
                        </section>
                      )
                    })}
                  </section>
                )
              )
            })}
        </Article>
        <SocialShare>
          <SocialShareBalloon>
            <Image alt="パパや友達にオススメしてね！" filename="balloon-share.png" />
          </SocialShareBalloon>
          <SocialShareButtonGroup>
            <SocialShareButton
              to={`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent('https://award.mamari.jp')}`}
            >
              <Image filename="share-line.png" />
            </SocialShareButton>
            <SocialShareButton
              to={`https://twitter.com/share?url=${encodeURIComponent(
                'https://award.mamari.jp'
              )}&text=${encodeURIComponent('4,415件の口コミから選ばれた、#ママリ口コミ大賞 アイテム一気見せ！→')}`}
            >
              <Image filename="share-twitter.png" />
            </SocialShareButton>
          </SocialShareButtonGroup>
        </SocialShare>
        <SidebarRight />
      </Container>
    </Layout>
  )
}

export const query = graphql`
  query($awardId: Int) {
    allMysqlCategories(filter: { parent_id: { eq: null } }, sort: { fields: mysqlId, order: ASC }) {
      edges {
        node {
          id
          mysqlId
          slug
          title
          categories {
            id
            mysqlId
            slug
            title
            fields {
              imageUrl
              pagePath
            }
          }
        }
      }
    }
    allMysqlRanks(filter: { award_id: { eq: $awardId } }, sort: { fields: rank, order: ASC }) {
      group(field: category_id) {
        fieldValue
        edges {
          node {
            id
            mysqlId
            category_id
            product_id
            rank
            ratings
            total_rating
            products {
              id
              mysqlId
              category_id
              name
              display_price
              manufacturer
              recommendation
              image
              fields {
                emblemImageUrl
                grandAwardImageUrl
                imageUrl
                pagePath
                withEmblemImageUrl
              }
            }
          }
        }
      }
    }
    allMysqlSurveyQuestions(filter: { award_id: { eq: $awardId } }) {
      edges {
        node {
          id
          mysqlId
          content
        }
      }
    }
    site {
      siteMetadata {
        title
      }
    }
  }
`
